import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE;

const ROLE_CONFIG = {
  ADMIN:                { bg: '#FCEBEB', color: '#791F1F', label: 'Admin' },
  OPERATOR:             { bg: '#FAEEDA', color: '#633806', label: 'Operator' },
  VIEWER:               { bg: '#E6F1FB', color: '#0C447C', label: 'Viewer' },
  PHOTO_REVIEWER:       { bg: '#F1EFE8', color: '#444',    label: 'Photo Reviewer' },
  WEIGHBRIDGE_OPERATOR: { bg: '#EAF3DE', color: '#27500A', label: 'Weighbridge Operator' },
  CHALAN_OPERATOR:      { bg: '#F3E8FF', color: '#5B21B6', label: 'Challan Operator' },
  PAYMENT_OPERATOR:     { bg: '#FCE4EC', color: '#9D174D', label: 'Payment Operator' },
};

function RolePill({ role }) {
  const cfg = ROLE_CONFIG[role] || { bg: '#F1EFE8', color: '#444', label: role };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 20,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {cfg.label}
    </span>
  );
}

export default function AdminPanel() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ username: '', password: '', fullName: '', role: 'OPERATOR', phone: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]         = useState('');

  async function loadOperators() {
    try {
      const res = await axios.get(`${API_BASE}/admin/operators`);
      setOperators(res.data);
    } catch {
      setToast('Failed to load operators');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOperators(); }, []);

  async function createOperator(e) {
    e.preventDefault();
    setFormError('');
    if (!form.username || !form.password || !form.fullName) {
      setFormError('Username, password and full name are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/admin/operators`, form);
      setToast(`✓ Operator ${form.username} created`);
      setShowForm(false);
      setForm({ username: '', password: '', fullName: '', role: 'OPERATOR', phone: '' });
      loadOperators();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create operator');
    } finally {
      setSubmitting(false);
    }
  }

  async function deactivate(id, username) {
    if (!window.confirm(`Deactivate ${username}? They will not be able to log in.`)) return;
    try {
      await axios.put(`${API_BASE}/admin/operators/${id}/deactivate`);
      setToast(`${username} deactivated`);
      loadOperators();
    } catch {
      setToast('Failed to deactivate');
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 8, fontSize: 13,
    background: '#FAFAF8', color: 'var(--text-primary)',
    outline: 'none', marginBottom: 12,
  };

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          padding: '12px 16px', background: '#EAF3DE',
          border: '1px solid #C0DD97', borderRadius: 10,
          fontSize: 13, color: '#27500A', fontWeight: 500,
        }}>
          {toast}
          <button onClick={() => setToast('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#27500A' }}>✕</button>
        </div>
      )}

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Operators</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{operators.length} total</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/material-prices" style={{
            padding: '10px 16px',
            background: '#fff', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 10,
            fontSize: 13, fontWeight: 600, textDecoration: 'none',
            display: 'flex', alignItems: 'center',
          }}>
            Material Prices
          </Link>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: '10px 16px',
              background: '#1C1C1A', color: '#fff',
              border: 'none', borderRadius: 10,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            + Add Operator
          </button>
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 14, padding: '20px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>
            New Operator
          </div>
          <form onSubmit={createOperator}>
            <input style={inputStyle} placeholder="Full Name *" value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })} />
            <input style={inputStyle} placeholder="Username * (e.g. ravi.kumar)" value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value.toLowerCase() })} />
            <input style={inputStyle} placeholder="Password * (min 6 chars)" type="password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} />
            <input style={inputStyle} placeholder="Phone (e.g. +919876543210)" value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ ...inputStyle, appearance: 'none' }}
            >
              <option value="ADMIN">Admin — Full access</option>
              <option value="OPERATOR">Operator — Review + approve/deny</option>
              <option value="VIEWER">Viewer — Read only</option>
              <option value="PHOTO_REVIEWER">Photo Reviewer — Photo review only</option>
              <option value="WEIGHBRIDGE_OPERATOR">Weighbridge Operator — Weighing only</option>
              <option value="CHALAN_OPERATOR">Challan Operator — records challans only</option>
              <option value="PAYMENT_OPERATOR">Payment Operator — takes payment only</option>
            </select>

            {formError && (
              <div style={{
                padding: '10px 12px', marginBottom: 12,
                background: '#FCEBEB', borderRadius: 8,
                fontSize: 12, color: '#791F1F',
              }}>
                {formError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="submit" disabled={submitting} style={{
                padding: '11px', background: '#1C1C1A', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
                {submitting ? 'Creating...' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={{
                padding: '11px', background: '#F4F3F0', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Operator list */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
        ) : operators.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No operators yet</div>
        ) : (
          operators.map((op, i) => (
            <div key={op.id} style={{
              padding: '16px 18px',
              borderBottom: i < operators.length - 1 ? '0.5px solid var(--border)' : 'none',
              opacity: op.active ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{op.fullName}</span>
                    <RolePill role={op.role} />
                    {!op.active && (
                      <span style={{ fontSize: 10, background: '#F1EFE8', color: '#888780', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                        Inactive
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>@{op.username}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Last login: {op.lastLoginAt === 'Never' ? 'Never' : new Date(op.lastLoginAt).toLocaleString('en-IN')}
                  </div>
                </div>
                {op.active && op.username !== 'admin' && (
                  <button
                    onClick={() => deactivate(op.id, op.username)}
                    style={{
                      padding: '6px 12px',
                      background: '#FCEBEB', color: '#791F1F',
                      border: '1px solid #F7C1C1',
                      borderRadius: 8, fontSize: 11,
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
